import { type DragEvent, type ReactNode, useCallback } from 'react'
import { DynamicIcon, IconName } from 'lucide-react/dynamic'
import { NODE_TYPE_DRAG_DATA_FORMAT } from '@/constants/flow-symbols'
import { useInsertNode } from '@/hooks/use-insert-node'
import { BuilderNodeType } from '../../../../types'

type NodePreviewDraggableProps = Readonly<{
  icon: IconName | ReactNode
  title: string
  description: string
  type: string
  children?: never
  setActivePanel: (
    panel: 'node-properties' | 'available-nodes' | 'none'
  ) => void
  insertNode: ReturnType<typeof useInsertNode>
  disabled?: boolean
}>

export function NodePreviewDraggable({
  icon,
  title,
  description,
  type,
  setActivePanel,
  insertNode,
  disabled = false,
}: NodePreviewDraggableProps) {
  const onDragStart = useCallback(
    (e: DragEvent, type: string) => {
      if (disabled) return
      e.dataTransfer.setData(NODE_TYPE_DRAG_DATA_FORMAT, type)
      e.dataTransfer.effectAllowed = 'move'
    },
    [disabled]
  )

  const onClick = useCallback(() => {
    if (disabled) return
    insertNode(type as BuilderNodeType)
    setActivePanel('none')
  }, [disabled, insertNode, setActivePanel, type])

  return (
    <div
      className={`flex select-none gap-2 rounded-xl border border-card-foreground/10 bg-card p-2.5 shadow-sm transition ${
        disabled
          ? 'cursor-not-allowed opacity-40'
          : 'cursor-grab hover:ring-2 hover:ring-primary/50'
      }`}
      onClick={onClick}
      onDragStart={(e) => onDragStart(e, type)}
      draggable={!disabled}
      data-vaul-no-drag
    >
      <div className='shrink-0'>
        <div className='flex items-center justify-center border size-10 rounded-xl border-card-foreground/10 bg-card'>
          {typeof icon === 'string' ? (
            <DynamicIcon
              name={icon as IconName}
              className='size-6 text-card-foreground/80'
            />
          ) : (
            icon
          )}
        </div>
      </div>

      <div className='flex flex-col ml-1 grow'>
        <div className='mt-px text-sm font-medium leading-normal'>{title}</div>
        <div className='mt-1 text-xs leading-normal line-clamp-3 text-card-foreground/60'>
          {description}
        </div>
      </div>
    </div>
  )
}
